import { Modal } from '@nextui-org/react';
import styled from 'styled-components';

import { Button, SecondaryButton } from '../';
import BottomSheet from '../BottomSheet';
import { ButtonRole } from '../Button';
import { BottomSheetChangeBreakpoint } from './Modal';

export const StyledModal = styled(Modal)`
  display: flex;
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    display: none;
  }
  &.nextui-modal {
    border-radius: 0;
    box-shadow: none !important;
    border: 1px solid ${({ theme }) => theme.colors.neutrals.c700};
  }
`;

export const StyledModalBody = styled(Modal.Body)<any>`
  padding: ${(props) => (props.$noPadding ? '0' : props.$padding ?? '2rem')};
  background: ${({ theme }) => theme.colors.neutrals.c800};
`;

export const StyledBottomSheet = styled(BottomSheet).attrs((props) => ({
  fullScreen: props.fullScreen,
  useTransparentBackground: props.useTransparentBackground,
  headerWithoutStyles: props.headerWithoutStyles,
  showDragIndicator: props.showDragIndicator,
  displayOnTablet: props.bottomSheetChangeBreakpoint === BottomSheetChangeBreakpoint.Tablet,
  zIndex: props.zIndex,
  ...props,
}))`
  display: none;

  [data-rsbs-footer] {
    box-shadow: none;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: ${(props) => (props.displayOnTablet ? 'flex' : 'none')};
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    display: flex;
  }
`;

interface ButtonProps {
  $buttonRole: ButtonRole;
  $color?: string;
}

export const StyledButton = styled(Button)<ButtonProps>`
  background-color: ${(props) => props.$buttonRole.backgroundColor(props.theme)};
  color: ${(props) => props.$buttonRole.color(props.theme)};
  border-color: ${(props) => props.$buttonRole.borderColor(props.theme)};
  &:hover {
    background-color: ${(props) => props.$buttonRole.backgroundColorHover(props.theme)};
    border-color: ${(props) => props.$buttonRole.backgroundColorHover(props.theme)};
    color: ${(props) => props.$buttonRole.colorHover(props.theme)};
  }
  &:active {
    background-color: ${(props) => props.$buttonRole.backgroundColorActive(props.theme)};
    color: ${(props) => props.$buttonRole.colorActive(props.theme)};
  }

  &:disabled {
    background: ${({ theme }) => theme.colors.neutrals.c600};
    opacity: 0.7;
    color: ${({ theme }) => theme.colors.neutrals.white};
    cursor: not-allowed;
  }

  ${(props) => props.$color != null && `background-color: ${props.$color};`};
  ${(props) => props.$color != null && `color: ${props.theme.colors.neutrals.white};`};
`;

export const StyledSecondaryButton = styled(SecondaryButton)<{
  $mobileRemoveBorder?: boolean;
  $buttonRole: any;
}>`
  background-color: ${(props) => props.$buttonRole.backgroundColor(props.theme)};
  border-color: ${(props) => props.$buttonRole.borderColor(props.theme)};
  &:hover {
    background-color: ${(props) => props.$buttonRole.backgroundColorHover(props.theme)};
    border-color: ${(props) => props.$buttonRole.backgroundColorHover(props.theme)};
    color: ${(props) => props.$buttonRole.colorHover(props.theme)};
  }
  &:active {
    background-color: ${(props) => props.$buttonRole.backgroundColorActive(props.theme)};
    color: ${(props) => props.$buttonRole.colorActive(props.theme)};
  }
  border-color: @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    ${(props) => props.$mobileRemoveBorder && 'border: none;'};
  }
`;

interface FooterProps {
  reverse?: boolean;
  $mobileColumnAlignment?: boolean;
}

export const StyledFooter = styled.div<FooterProps>`
  display: flex;
  flex-direction: ${({ reverse }) => (reverse ? 'row-reverse' : 'row')};
  width: 100%;
  gap: 1rem;
  justify-content: ${({ reverse }) => (reverse ? 'flex-start' : 'flex-end')};
  padding: 0 2rem 1.5rem 2rem;
  background: ${({ theme }) => theme.colors.neutrals.c800};
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ $mobileColumnAlignment }) => ($mobileColumnAlignment ? '0 0.5rem;' : '2rem 0 2rem 0;')};
    gap: 0.25rem;
    flex-direction: ${({ reverse, $mobileColumnAlignment }) =>
      reverse ? 'row-reverse' : $mobileColumnAlignment ? 'column-reverse' : 'row'};
    button {
      max-height: 2.5rem;
    }
    gap: ${({ $mobileColumnAlignment }) => ($mobileColumnAlignment ? '1rem;' : '0.5rem;')};
  }
`;

export const StyledGhostButton = styled(SecondaryButton)`
  background-color: transparent;
  color: ${({ theme }) => theme.colors.neutrals.white};
  margin-right: auto;
  border: 1px solid ${({ theme }) => theme.colors.neutrals.white};
  font-size: ${({ theme }) => theme.fontVariants.desktop.buttons.md.fontSize};
  line-height: ${({ theme }) => theme.fontVariants.desktop.buttons.md.lineHeight};
  &:hover {
    background-color: ${({ theme }) => theme.colors.neutrals.white};
    color: ${({ theme }) => theme.colors.neutrals.black};
  }
  &:focus {
    background-color: ${({ theme }) => theme.colors.neutrals.c100};
    color: ${({ theme }) => theme.colors.neutrals.black};
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    margin: 0;
  }
`;
